import { GUIDE_PROMPTS } from "./guidePrompts.js";

export const OPPORTUNITY_PROMPT = "Help me find the most useful opportunity to work on this week. First establish the business goal and which relevant data you can access. Then identify up to three opportunities, explain the evidence with real data and uncertainty, and recommend a practical next main goal.";

// Adapt Get Better Results briefs to a conversation, while keeping each
// source guide's specific review boundary in one place.
function guideTask({ id, category, title, detail, goal, context, work }) {
  return {
    id,
    category,
    title,
    detail,
    guideId: id,
    prompt: [
      goal,
      `First check which relevant data and approved brand materials you can actually access. Use existing context and ask me only for the missing essentials: ${context}. Name unavailable or stale sources, separate evidence from assumptions, and do not invent figures or customer claims.`,
      work,
      GUIDE_PROMPTS[id].close,
    ].join("\n\n"),
  };
}

export const CHAT_TASKS = [
  {
    id: "opportunity",
    category: "Growth",
    title: "Find this week’s best opportunity",
    detail: "Use real business data to choose one practical goal worth pursuing.",
    guideId: "growth-bottlenecks",
    prompt: OPPORTUNITY_PROMPT,
  },
  guideTask({
    id: "weekly-growth-report",
    category: "Growth",
    title: "Review this week’s performance",
    detail: "See what moved, what explains it, and which actions deserve attention.",
    goal: "Help me understand how the business performed this week and what to do next.",
    context: "the reporting period, comparison period, business goal, and metrics that matter most",
    work: "Build a concise report from the available sources, with revenue, spend, conversion rate, average order value, repeat purchases, and email revenue where verifiable and relevant. Show each metric's comparison and source freshness. Explain material changes, distinguish observed facts from possible causes, and recommend up to three next actions with the evidence and uncertainty behind each. Return the report and proposed actions for review before starting any follow-up work.",
  }),
  guideTask({
    id: "product-launch",
    category: "Growth",
    title: "Plan a product launch",
    detail: "Turn a launch goal into a timeline, asset list, and clear dependencies.",
    goal: "Help me plan a product launch with the decisions and dependencies visible before we start building.",
    context: "the product, launch goal and target date, audience, available channels, offer, and team capacity",
    work: "Review the product information, stock readiness, and existing launch assets you can access. Propose a realistic timeline working back from the date, showing what depends on what and which items could block launch. List the pages, messages, creative, and measurement needed, with owners to confirm and a success measure. Keep unknowns visible and bring me the timeline for review before drafting the individual assets.",
  }),
  guideTask({
    id: "product-page",
    category: "Store",
    title: "Improve a product page",
    detail: "Find unanswered objections and draft clearer copy backed by proof.",
    goal: "Help me improve one product page so customers understand the value and feel confident buying.",
    context: "the product page URL, intended customer, desired action, and any known purchase objections",
    work: "Review the page and any accessible performance data, customer reviews, support questions, and approved product claims. Identify where the benefit, proof, offer, or path to purchase is unclear, including on mobile. Rank the changes by likely impact and effort, stating which findings are evidenced and which need testing. Draft a stronger section order and the most useful copy changes, using only supportable claims, so I can review a concrete proposal.",
  }),
  guideTask({
    id: "product-bundles",
    category: "Store",
    title: "Build better product bundles",
    detail: "Propose useful combinations with pricing, margin, and stock considered.",
    goal: "Help me raise average order value with product bundles that solve a real customer need.",
    context: "which products can be bundled, the order value target, product costs, margin limits, and stock constraints",
    work: "Inspect the product catalogue, available inventory, and purchase combinations where accessible. Propose up to three bundles, each built around a customer goal, and explain why the products belong together. Show a pricing and margin calculation only where the inputs are verified; otherwise list the missing inputs. Compare the trade-offs and recommend one bundle to test, with a draft merchandising angle and a way to measure whether it helps.",
  }),
  guideTask({
    id: "email-performance-audit",
    category: "Email",
    title: "Audit email performance",
    detail: "Look beyond revenue to find weak sends, flows, or audience signals.",
    goal: "Help me understand the health of our email programme and the most useful improvement to make next.",
    context: "the reporting and comparison periods, campaigns or flows to review, business goal, and any metric of concern",
    work: "Review available campaign and flow data, reporting revenue alongside clicks, conversion, unsubscribes, and deliverability signals. State metric definitions or attribution limits that affect the comparison. Identify the strongest and weakest patterns with actual evidence, explain what you cannot establish, and rank the next improvements by likely value and effort. Recommend a focused next-send test with a clear success measure for me to review.",
  }),
  guideTask({
    id: "message-campaign",
    category: "Email",
    title: "Draft an email campaign",
    detail: "Get a coordinated sequence with subject lines, copy, and send timing.",
    goal: "Help me draft an email campaign around one clear business goal.",
    context: "the promotion or announcement, audience, offer and constraints, desired action, and proposed send window",
    work: "Check accessible past sends and performance, approved brand examples, product facts, and offer details. Recommend an appropriate sequence and explain its purpose before drafting the messages. Include subject line options, preview text, body copy, one clear call to action per email, and proposed audience and timing. Keep claims supported, mark anything that needs confirmation, and explain what we should measure to learn from the campaign.",
  }),
  guideTask({
    id: "winback-campaign",
    category: "Email",
    title: "Win back lapsed customers",
    detail: "Define the audience and draft a sequence that respects your margins.",
    goal: "Help me plan a winback campaign for customers who have stopped buying.",
    context: "what counts as lapsed for our products, exclusions, allowable offers and margin limits, and products we can recommend",
    work: "Inspect available purchase history, repeat-purchase timing, prior offers, and customer feedback. Propose precise audience rules and exclusions for review. Build a short message sequence that starts with relevance and useful product recommendations, introducing or escalating an incentive only where justified. Base recommendations on purchase behaviour where available, state assumptions, and include draft copy, timing, and a practical measure of incremental success.",
  }),
  guideTask({
    id: "ad-hooks",
    category: "Content",
    title: "Write ad hooks worth testing",
    detail: "Explore distinct customer motivations using claims you can support.",
    goal: "Help me write distinct ad hooks that test why our customers would care about this product.",
    context: "the product, audience, problem it solves, target channel, and number of hooks needed",
    work: "Review available product facts, approved claims, customer language, testimonials, and past creative results. Group the hooks by distinct motivations or styles, such as problem, benefit, testimonial, offer, comparison, and founder story, using only styles the evidence supports. Explain what each group tests and name the proof needed for any unverified angle. Recommend a small first test set and how we could compare it; return text drafts rather than producing final creative.",
  }),
  guideTask({
    id: "social-batch",
    category: "Content",
    title: "Plan a social content batch",
    detail: "Build a useful posting calendar with platform-specific draft captions.",
    goal: "Help me plan a social content batch around one useful business objective.",
    context: "the objective, audience, channels, topic or product, date range, number of posts, and approved source assets",
    work: "Inspect accessible brand guidance, product information, reusable assets, and recent content performance. Propose a coherent set of post concepts with a distinct purpose for each. Draft captions and calls to action suited to each platform, identify the existing asset or asset brief each needs, and organise the batch into a reviewable calendar. Keep product claims supported and label missing assets or decisions so the plan is practical to use.",
  }),
];

export const CHAT_TASK_CATEGORIES = [...new Set(CHAT_TASKS.map((task) => task.category))];

export const CHAT_STARTER_IDS = ["opportunity", "product-page", "message-campaign"];
